import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Stack, TextField } from '@mui/material';
import { theme } from '../../../constants/theme';
import { useThemeContext } from '../../../hook/useThemeManager';
import Cancel from '@mui/icons-material/Cancel';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { IAdvancedContest, IProject } from '../../../types/global/helper';
import { ShortSegmentedControl } from '../../SegmentedControl/ShortSegmentedControl';
import { Spinner } from '@chakra-ui/react';
import { AvatarShimmer } from '../EsMyProfileContainer/AvatarShimmer';
import CustomButton from '../../Button/CustomButton';
import { getUsersUnsubmittedProjects, submitProject } from '../../../redux/contest/actions';
import { usersUnsubmittedProjectSelector } from '../../../selectors/selectors';
import { ProjectAvatar } from '../../ProjectAvatar/ProjectAvatar';
import { isEmpty } from '../../../utils/lodash';
import { PillSelector } from '../../PillSelector/PillSelector';

export interface IJuryDialogueModal {
    placeholder?: string;
    open: boolean;
    onClose?: () => void;
    contestId: string;
    contest?: IAdvancedContest;
}

/**
 *
 * @param param0
 * @returns
 *
 * I will attempt to handle the api call and redux operation for this module inside here
 * As I see that it only has one functionality, which is to search and select users.
 * In the future we will move it out of here to the parent to keep it uniform.
 */

const tabList = ['host entry', 'late submission requests'];
enum TABLIST {
    HOST_ENTRY = 'host entry',
    LATE_SUBMISSION_REQUEST = 'late submission requests',
}

type TabSelector = 'host entry' | 'late submission requests';
const HostEntryDialogue: React.FC<IJuryDialogueModal> = ({ placeholder, open, onClose, contestId, contest }) => {
    const [searchValue, setSearchValue] = useState('');
    const [visibleTab, setVisibleTab] = useState<TabSelector>(TABLIST.HOST_ENTRY);
    const [category, setCategory] = useState<string[]>([]);
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const { currentPage, projects, loading } = useSelector(usersUnsubmittedProjectSelector);
    const scrollRef = useRef<HTMLDivElement>(null);
    const offset = 20;

    const handleAvatarClick = (avatar: IProject) => {
        onClose && onClose();
    };

    const handleDebouncedSearch = useRef(
        _.debounce((searchTerm: string) => {
            if (searchTerm.length > 4 && !loading) {
                dispatch(getUsersUnsubmittedProjects({ searchTerm, contestId, page: 1, sortBy: 'asc' }));
            }

            if (searchTerm.length < 1 && !loading) {
            }
        }, 1000),
    ).current;

    const handleSearchInputChange = (event: { target: { value: string } }) => {
        if (visibleTab === TABLIST.HOST_ENTRY) {
            setSearchValue(event.target.value);
            handleDebouncedSearch(event.target.value);
            if (event.target.value.length < 1) {
                dispatch(getUsersUnsubmittedProjects({ contestId, page: 1, sortBy: 'asc' }));
            }
        }
    };

    const handleOnEndReach = () => {
        dispatch(
            getUsersUnsubmittedProjects({ searchTerm: searchValue, contestId, page: currentPage + 1, sortBy: 'asc' }),
        );
    };

    const handleScroll = () => {
        const box = scrollRef.current;
        if (box) {
            const { scrollTop, scrollHeight, clientHeight } = box;
            if (scrollTop + clientHeight >= scrollHeight - offset) {
                handleOnEndReach();
            }
        }
    };

    const handleGetUnsubmitted = useCallback(() => {
        dispatch(
            getUsersUnsubmittedProjects({
                contestId: contestId,
                page: 1,
            }),
        );
    }, [dispatch, contestId]);

    useEffect(() => {
        if (visibleTab === TABLIST.HOST_ENTRY) {
            handleGetUnsubmitted();
        }
    }, [visibleTab, handleGetUnsubmitted]);

    const handleChangeTab = (tab: string) => {
        setVisibleTab(tab as TabSelector);
        setSearchValue('');
        if (tab === TABLIST.HOST_ENTRY) {
            handleGetUnsubmitted();
        }
        if (tab === TABLIST.LATE_SUBMISSION_REQUEST) {
            /** IMplement later */
        }
    };

    const handleSubmitEntry = (project: IProject) => {
        /** Create a submission */
        dispatch(
            submitProject({
                contest: contestId,
                project: project._id,
                isHostSubmission: true,
            }),
        );
    };

    const handleSetCategory = (project: IProject, category: string[]) => {
        setCategory(category);
        dispatch(
            submitProject({
                contest: contestId,
                project: project._id,
                isHostSubmission: true,
                category: category[0],
            }),
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            color="blue"
            PaperProps={{
                style: {
                    backgroundColor: theme[themeStyle].deep_grey_lighter,
                    overflowY: 'scroll',
                    border: '1px solid grey',
                    maxHeight: '100vh',
                    margin: 0,
                },
                sx: {
                    height: {
                        xs: '100vh',
                        sm: '70%',
                    },
                    width: {
                        xs: '100vw',
                        sm: '50%',
                        md: '40%',
                    },
                    borderRadius: {
                        xs: 3,
                        sm: 2,
                        md: 5,
                    },
                },
            }}
            sx={{ backgroundColor: theme[themeStyle].modal_dark }}>
            <ShortSegmentedControl
                tabs={tabList}
                onHandleClick={handleChangeTab}
                initialIndex={tabList.indexOf(visibleTab)}
                sx={{
                    width: '100%',
                }}
                tabStyle={{
                    width: '100%',
                }}
                buttonStyle={{
                    width: '100%',
                }}
            />

            <TextField
                autoFocus
                variant="outlined"
                placeholder={placeholder ?? 'Search users...'}
                color="secondary"
                InputProps={{
                    placeholder: placeholder ?? `Search ${visibleTab}...`,
                    style: {
                        color: theme[themeStyle].tag_white, // Change the placeholder color to white
                        border: '0.5px solid white', // Set the border width and color
                        borderRadius: '50px', // Set the border radius
                        paddingLeft: '8px', // Add some padding for the text inside the input field
                        fontSize: 12,
                        minWidth: 200,
                    },
                }}
                inputProps={{
                    color: 'blue',
                }}
                // Add your search functionality here
                onChange={e => {
                    handleSearchInputChange(e);
                }}
                sx={{
                    px: 3,
                    pt: 3,
                    pb: 1,
                }}
            />
            <DialogContent ref={scrollRef} onScroll={handleScroll}>
                {visibleTab === TABLIST.HOST_ENTRY && (
                    <>
                        {loading && new Array(20).fill('X').map((_, i) => <AvatarShimmer key={i} />)}
                        {!!projects &&
                            !loading &&
                            !_.isEmpty(projects) &&
                            projects.map((project, index) => (
                                <Stack
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    key={index}
                                    mb={1}>
                                    <Stack flexDirection="row" alignItems="center">
                                        <ProjectAvatar
                                            key={index}
                                            project={project}
                                            onClick={() => handleAvatarClick(project)}
                                        />
                                    </Stack>
                                    {contest?.categories_enabled &&
                                    !!contest?.categories_enabled &&
                                    isEmpty(contest?.categories_enabled) ? (
                                        <PillSelector
                                            value={category}
                                            onChange={c => handleSetCategory(project, c)}
                                            options={contest?.categories}
                                            overrideSubmit
                                            maxSelection={1}
                                            normalButton
                                            containerStyle={{
                                                width: 'fit-content',
                                                marginRight: 0,
                                            }}
                                            buttonProps={{
                                                sx: {
                                                    mr: 0,
                                                },
                                                label: 'Submit',
                                            }}
                                        />
                                    ) : (
                                        <CustomButton
                                            onClick={() => handleSubmitEntry(project)}
                                            customStyle={{
                                                width: '100px',
                                            }}
                                            label={true ? 'Submit' : 'Submitted'}
                                            textStyle={{
                                                fontSize: 12,
                                            }}
                                        />
                                    )}
                                </Stack>
                            ))}
                    </>
                )}

                {visibleTab === TABLIST.LATE_SUBMISSION_REQUEST && (
                    <>{loading && new Array(20).fill('X').map((_, i) => <AvatarShimmer key={i} />)}</>
                )}
            </DialogContent>

            {false && <Spinner pos="absolute" color="white" size="xl" right={'50%'} bottom={10} h={5} w={5} />}

            <DialogActions sx={{ position: 'absolute', right: 10, bottom: 10 }}>
                <IconButton
                    color="primary"
                    aria-label="Add Avatar"
                    onClick={onClose}
                    style={{
                        borderRadius: '50%',
                        borderWidth: 1,
                        borderColor: theme[themeStyle].lightWhite,
                        width: 30,
                    }}>
                    <Cancel sx={{ color: theme[themeStyle].true_white }} />
                </IconButton>
            </DialogActions>
        </Dialog>
    );
};

export { HostEntryDialogue };
